import { supabase } from '@/lib/supabase';
import { forgeWorkerUrl, invokeForgeWorker } from '@/lib/forge-worker-client';
import { invokeEdgeFunctionStream } from '@/lib/sse-client';
import { parseQualificationSheet, type CompanyQualificationSheet } from '@shared/types/company-qualification';
import type { AnalystMessage, AnalystReadRequest, AnalystReading, AnalystScope } from '@shared/types/company-analyst';

/** Same staleness rule as the worker: a turn silent for longer is gone. */
const TURN_STALE_MS = 90_000;

export interface LoadedAnalystConversation {
  messages: AnalystMessage[];
  turnRunning: boolean;
}

export const loadAnalystConversation = async (scope: AnalystScope): Promise<LoadedAnalystConversation> => {
  const { data, error } = await supabase.from('qualification_conversations')
    .select('messages, turn_heartbeat_at').eq('scope', scope).maybeSingle();
  if (error) throw new Error('La conversation n’est pas accessible pour le moment.');
  const beat = data?.turn_heartbeat_at ? Date.parse(data.turn_heartbeat_at as string) : null;
  return {
    messages: Array.isArray(data?.messages) ? data!.messages as AnalystMessage[] : [],
    turnRunning: beat !== null && Date.now() - beat < TURN_STALE_MS,
  };
};

export interface AnalystTurnHandlers {
  onPhase: (phase: 'read' | 'write') => void;
  onDelta: (text: string) => void;
  onReasoning?: (text: string) => void;
  onReadings: (payload: { pending?: AnalystReadRequest[]; readings?: AnalystReading[] }) => void;
}

export interface AnalystTurnOutcome {
  messages: AnalystMessage[];
  sheet: CompanyQualificationSheet;
  version: number;
  /** The analyst closed this step: its restitution can be written. */
  closed: boolean;
}

const record = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? value as Record<string, unknown> : {};

/** Runs one turn of the analyst (streamed) and returns the saved sheet. */
export const runAnalystTurn = async (
  scope: AnalystScope, message: string | null, handlers: AnalystTurnHandlers,
): Promise<AnalystTurnOutcome> => {
  if (!forgeWorkerUrl) throw new Error('L’analyste n’est pas configuré sur cet environnement (VITE_FORGE_WORKER_URL).');
  let outcome: AnalystTurnOutcome | null = null;
  let failure: string | null = null;
  await invokeEdgeFunctionStream({
    url: forgeWorkerUrl,
    body: { action: 'analyst_turn', scope, message },
    onEvent: (name, data) => {
      const payload = record(data);
      if (name === 'progress' && (payload.stage === 'read' || payload.stage === 'write')) handlers.onPhase(payload.stage);
      else if (name === 'delta' && typeof payload.delta === 'string') handlers.onDelta(payload.delta);
      else if (name === 'reasoning' && typeof payload.summary === 'string') handlers.onReasoning?.(payload.summary);
      else if (name === 'checks') {
        handlers.onReadings({
          pending: Array.isArray(payload.pending) ? payload.pending as AnalystReadRequest[] : undefined,
          readings: Array.isArray(payload.checks) ? payload.checks as AnalystReading[] : undefined,
        });
      } else if (name === 'complete') {
        outcome = {
          messages: Array.isArray(payload.messages) ? payload.messages as AnalystMessage[] : [],
          sheet: parseQualificationSheet(payload.sheet).sheet,
          version: typeof payload.version === 'number' ? payload.version : 0,
          closed: payload.closed === true,
        };
      } else if (name === 'error') {
        failure = typeof payload.message === 'string' ? payload.message : 'La réponse de l’analyste n’a pas abouti.';
      }
    },
  });
  if (failure) throw new Error(failure);
  if (!outcome) throw new Error('La réponse de l’analyste a été coupée. Rechargez pour voir où il en est.');
  return outcome;
};

export const stopAnalystTurn = async (scope: AnalystScope): Promise<void> => {
  await invokeForgeWorker({ action: 'analyst_stop', scope });
};
